import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormAlert } from "@/components/common/Alert";
import { useCatalogue } from "@/context/CatalogueContext";
import { api, errorMessage } from "@/lib/api";

const splitIds = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

function useSaver(path, build) {
  const { reload } = useCatalogue();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (rows) => {
    setBusy(true);
    setError("");
    try {
      await api.put(path, build(rows));
      await reload();
      toast.success("Saved");
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return { save, error, busy };
}

export const TaskGroupsEditor = () => {
  const { task_groups: groups } = useCatalogue();
  const [rows, setRows] = useState([]);
  useEffect(() => { setRows(groups.map((g) => ({ name: g.name, items: g.items.join(", ") }))); }, [groups]);
  const { save, error, busy } = useSaver("/admin/task-groups", (r) => r.map((x) => ({ name: x.name, items: splitIds(x.items) })));
  const set = (i, k, v) => setRows((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const move = (i, d) => setRows((r) => { const n = [...r]; const t = n[i + d]; if (!t) return r; n[i + d] = n[i]; n[i] = t; return n; });
  return (
    <div className="space-y-3" data-testid="task-groups-editor">
      <p className="text-sm text-muted-foreground">Each task group is a card on Home. List the emp_nos and workflow ids under it, separated by commas. Order here is the order on Home.</p>
      <FormAlert>{error}</FormAlert>
      {rows.map((r, i) => (
        <div key={i} className="grid gap-2 rounded-xl border bg-card p-3 md:grid-cols-[220px_1fr_auto]" data-testid={`task-group-row-${i}`}>
          <Input value={r.name} maxLength={80} onChange={(e) => set(i, "name", e.target.value)} aria-label="Task group name" data-testid={`task-group-name-${i}`} />
          <Input value={r.items} onChange={(e) => set(i, "items", e.target.value)} aria-label="Agents and workflows" data-testid={`task-group-items-${i}`} />
          <div className="flex gap-1">
            <Button type="button" variant="ghost" size="icon" onClick={() => move(i, -1)} aria-label="Move up"><ArrowUp className="!h-4 !w-4" /></Button>
            <Button type="button" variant="ghost" size="icon" onClick={() => move(i, 1)} aria-label="Move down"><ArrowDown className="!h-4 !w-4" /></Button>
            <Button type="button" variant="ghost" size="icon" onClick={() => setRows((x) => x.filter((_, j) => j !== i))} aria-label="Remove task group" data-testid={`task-group-remove-${i}`}><Trash2 className="!h-4 !w-4" /></Button>
          </div>
        </div>
      ))}
      <div className="flex gap-2">
        <Button variant="secondary" onClick={() => setRows((r) => [...r, { name: "", items: "" }])} data-testid="task-group-add"><Plus className="!h-4 !w-4" /> Add task group</Button>
        <Button onClick={() => save(rows)} disabled={busy || rows.some((r) => !r.name.trim())} data-testid="task-groups-save">{busy ? "Saving" : "Save task groups"}</Button>
      </div>
    </div>
  );
};

export const NextStepsEditor = () => {
  const { next_steps: steps } = useCatalogue();
  const [rows, setRows] = useState([]);
  useEffect(() => { setRows(steps.map((s) => ({ from_id: s.from_id, to_ids: s.to_ids.join(", "), label: s.label }))); }, [steps]);
  const { save, error, busy } = useSaver("/admin/next-steps", (r) => r.map((x) => ({ from_id: x.from_id, to_ids: splitIds(x.to_ids), label: x.label })));
  const set = (i, k, v) => setRows((r) => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="space-y-3" data-testid="next-steps-editor">
      <p className="text-sm text-muted-foreground">Shown in the What next panel after a run. From one id to one or more ids (comma separated), with the label people see.</p>
      <FormAlert>{error}</FormAlert>
      {rows.map((r, i) => (
        <div key={i} className="grid gap-2 rounded-xl border bg-card p-3 md:grid-cols-[120px_180px_1fr_auto]" data-testid={`next-step-row-${i}`}>
          <Input value={r.from_id} onChange={(e) => set(i, "from_id", e.target.value)} aria-label="From id" placeholder="From" data-testid={`next-step-from-${i}`} />
          <Input value={r.to_ids} onChange={(e) => set(i, "to_ids", e.target.value)} aria-label="To ids" placeholder="To" data-testid={`next-step-to-${i}`} />
          <Input value={r.label} maxLength={120} onChange={(e) => set(i, "label", e.target.value)} aria-label="Label" placeholder="Label" data-testid={`next-step-label-${i}`} />
          <Button type="button" variant="ghost" size="icon" onClick={() => setRows((x) => x.filter((_, j) => j !== i))} aria-label="Remove next step"><Trash2 className="!h-4 !w-4" /></Button>
        </div>
      ))}
      <div className="flex gap-2">
        <Button variant="secondary" onClick={() => setRows((r) => [...r, { from_id: "", to_ids: "", label: "" }])} data-testid="next-step-add"><Plus className="!h-4 !w-4" /> Add next step</Button>
        <Button onClick={() => save(rows)} disabled={busy || rows.some((r) => !r.from_id.trim() || !r.to_ids.trim() || !r.label.trim())} data-testid="next-steps-save">{busy ? "Saving" : "Save next steps"}</Button>
      </div>
    </div>
  );
};
